import { onBoardPawns, opponent, sameSquare, squareIndex } from './board';
import { ALL_FACES, faceMatches } from './cards';
import {
  checkRecordedMove,
  crownsAt,
  generateMoves,
  hasMove,
  withoutPawn,
  type BoardCtx,
  type MoveFailure,
  type MoveOption,
} from './movement';
import {
  FREE_COLLATERAL,
  pathLength,
  afterCapture,
  afterChainScored,
  afterCrowning,
  afterLinkMove,
  stepThroughGlass,
  afterPawning,
  applyTallyEffects,
  captureInfo,
  endOfMove,
  epitaphRecipient,
  freeWildAvailable,
  nextArrivals,
  notePass,
  noteFallen,
  onEpitaphReceived,
  onRecoverDraw,
  queueArrival,
  returnStolen,
  returningCards,
  roundEnd,
  roundSetup,
  skipsTurn,
  staysAsCollateral,
  turnEnd,
  turnStart,
  twinSoul,
  useFreeWild,
} from './ledger/flow';
import { glassTwin, litBrazier, omenAt, omensAt } from './ledger/hooks';
import { darkFiles, horrorsAtTurnStart, inTheDark } from './ledger/horrors';
import {
  L,
  bumpCounter,
  epitaphPlays,
  epitaphSlots,
  handLimit,
  hasRule,
  hasStatus,
  holds,
  markOnce,
  maxLinks,
  turnLimitRule,
} from './ledger/state';
import { tally, type TallyInput } from './ledger/tally';
import { STILL, applyTrick, endTrickStatuses, trickTargets } from './ledger/tricks';
import type { TrickId } from './ledger/ids';
import { linkNotation } from './notation';
import { Pcg32 } from './rng';
import { scoreChain } from './scoring';
import { boardCtx, squareKey, topFace } from './state';
import type {
  Card,
  Epitaph,
  Face,
  Inscription,
  LedgerLink,
  MatchEvent,
  MatchResult,
  MatchState,
  PassKind,
  Pawn,
  RecordedLink,
  RouteStep,
  ScoreBreakdown,
  Side,
  Square,
  TrickTarget,
} from './types';

/**
 * Rules procedures (spec §4–§12). Every function that takes a MatchState named `s` and an event
 * list mutates that state in place; callers must pass a clone (see applyAction).
 */

type Events = MatchEvent[];

// ---------------------------------------------------------------------------------------------
// Links and playability (§7.4, §9)
// ---------------------------------------------------------------------------------------------

/** A fully specified legal link. */
export interface LinkOption {
  readonly cardId: string;
  readonly pawnId: string;
  readonly face: Face;
  readonly wasWild: boolean;
  readonly collateralPawnId: string | null;
  readonly option: MoveOption;
  /** Long Night: a Trick's link, which moves nothing (its `option` is STILL). */
  readonly trick?: { readonly id: TrickId; readonly target: TrickTarget };
}

/**
 * Legal collateral for a Wild that moves `chainPawnId` (§9.4, §15.4). In the Long Night, Pinned
 * pawns cannot pay, Heavy Toll keeps reserves out, and Pawn Ticket or a Rag Doll pay for free.
 */
export function collateralChoices(
  s: MatchState,
  side: Side,
  chainPawnId: string,
): { onBoard: string[]; reserves: string[]; free: boolean } {
  const pl = s.players[side];
  const ledger = s.ledger !== undefined;
  return {
    onBoard: onBoardPawns(s.pawns, side)
      .filter(
        (p) =>
          p.id !== chainPawnId &&
          !(ledger && (hasStatus(s, p.id, 'pinned') || (p.square && inTheDark(s, p.square)))),
      )
      .map((p) => p.id),
    reserves:
      ledger && hasRule(s, 'heavy-toll', side)
        ? []
        : pl.reserves.filter((id) => !pl.reservesPawnedThisRound.includes(id)),
    free: ledger && freeWildAvailable(s, side),
  };
}

/** Pawns that may make the next link: any on-board pawn for a first link, else the chain pawn. */
export function movablePawns(s: MatchState, side: Side): Pawn[] {
  if (s.chain) {
    const p = s.pawns[s.chain.pawnId];
    return p && p.status === 'onBoard' ? [p] : [];
  }
  const pawns = onBoardPawns(s.pawns, side);
  // Long Night: Pinned pawns stay; under Blind Eye so do pawns in the dark half.
  return s.ledger
    ? pawns.filter((p) => !hasStatus(s, p.id, 'pinned') && !(p.square && inTheDark(s, p.square)))
    : pawns;
}

/** Blind Eye: no landing may fall in the dark half of the board. */
const litLandings = (s: MatchState, o: LinkOption): boolean =>
  !s.ledger || !o.option.landings.some((sq) => inTheDark(s, sq));

/**
 * Whether a pawn has a move with a face whose landings are all lit: `hasMove`, unless Blind Eye
 * darkens half the board, when every move is looked at as `litLandings` does. Playability and the
 * legal links must agree, or a turn could offer a card that cannot be played (and never pass).
 */
function hasLitMove(s: MatchState, ctx: BoardCtx, pawn: Pawn, face: Face): boolean {
  const dark = s.ledger ? darkFiles(s) : null;
  if (!dark) return hasMove(ctx, pawn, face);
  return generateMoves(ctx, pawn, face).some((o) => !o.landings.some((sq) => dark.includes(sq.x)));
}

/** Whether a card may be played on the top (§7.3; Saint's Finger and Chalk Stamps match anything). */
export function cardMatches(s: MatchState, side: Side, card: Card, face: Face): boolean {
  if (faceMatches(face, topFace(s))) return true;
  if (!s.ledger) return false;
  // From a lit Brazier any card goes: it burns.
  if (litBrazier(s, side)) return true;
  if (holds(s, side, 'saints-finger')) return true;
  const stamp = s.ledger.cardStamps[card.id] ?? card.stamp;
  return stamp === 'chalk-stamp' && !hasRule(s, 'gleam', side);
}

/** Single File: after its first chain of a round, a side's chains begin with that chain's color. */
export function firstLinkColorOk(s: MatchState, side: Side, face: Face): boolean {
  if (!s.ledger || s.chain || !hasRule(s, 'single-file', side)) return true;
  const first = s.ledger.round.firstColor[side];
  return !first || face.color === first;
}

/** Every legal link option for one card in hand (§7.4, §16.6). */
export function linkOptionsForCard(
  s: MatchState,
  side: Side,
  cardId: string,
  onlyPawnId?: string,
): LinkOption[] {
  const out: LinkOption[] = [];
  const card = s.cards[cardId];
  if (!card || card.owner !== side || card.iou) return out;
  const ctx = boardCtx(s);
  let pawns = movablePawns(s, side);
  if (onlyPawnId !== undefined) pawns = pawns.filter((p) => p.id === onlyPawnId);
  if (card.trick) return trickOptions(s, side, card, pawns);
  if (card.kind === 'normal') {
    const face = card.face as Face;
    if (!cardMatches(s, side, card, face) || !firstLinkColorOk(s, side, face)) return out;
    for (const pawn of pawns)
      for (const option of generateMoves(ctx, pawn, face))
        out.push({ cardId, pawnId: pawn.id, face, wasWild: false, collateralPawnId: null, option });
    return out.filter((o) => litLandings(s, o));
  }
  const faces = ALL_FACES.filter((f) => firstLinkColorOk(s, side, f));
  for (const pawn of pawns) {
    const { onBoard, reserves, free } = collateralChoices(s, side, pawn.id);
    for (const collateralId of onBoard) {
      // A pawn that pays without leaving the board (Many Lives) leaves it as it is.
      const cctx = staysAsCollateral(s, s.pawns[collateralId])
        ? ctx
        : withoutPawn(ctx, collateralId);
      for (const face of faces)
        for (const option of generateMoves(cctx, pawn, face))
          out.push({
            cardId,
            pawnId: pawn.id,
            face,
            wasWild: true,
            collateralPawnId: collateralId,
            option,
          });
    }
    const unchanged = free ? [...reserves, FREE_COLLATERAL] : reserves;
    if (unchanged.length > 0) {
      // A reserve leaves the board unchanged, so every reserve allows the same moves.
      const byFace = faces.map((face) => ({ face, moves: generateMoves(ctx, pawn, face) }));
      for (const collateralId of unchanged)
        for (const { face, moves } of byFace)
          for (const option of moves)
            out.push({
              cardId,
              pawnId: pawn.id,
              face,
              wasWild: true,
              collateralPawnId: collateralId,
              option,
            });
    }
  }
  return out.filter((o) => litLandings(s, o));
}

/** A Trick's links (Long Night): one per chain pawn and target; the pawn stays where it is. */
function trickOptions(s: MatchState, side: Side, card: Card, pawns: readonly Pawn[]): LinkOption[] {
  const trick = card.trick;
  if (!s.ledger || !trick || card.kind !== 'normal') return [];
  const printed = card.face as Face;
  if (!cardMatches(s, side, card, printed)) return [];
  const faceOk = (f: Face) => firstLinkColorOk(s, side, f);
  const out: LinkOption[] = [];
  for (const pawn of pawns)
    for (const target of trickTargets(s, side, trick, pawn, faceOk)) {
      const face = target.face ?? printed;
      if (!target.face && !faceOk(face)) continue;
      out.push({
        cardId: card.id,
        pawnId: pawn.id,
        face,
        wasWild: false,
        collateralPawnId: null,
        option: STILL,
        trick: { id: trick, target },
      });
    }
  return out;
}

export type Playability =
  | { readonly playable: true }
  | {
      readonly playable: false;
      readonly reason:
        'NO_MATCH' | 'NO_LEGAL_MOVE' | 'NO_COLLATERAL' | 'NOT_IN_HAND' | 'NOT_YOUR_TURN';
    };

/** Whether a card in hand can be played as the next link, and why not (§7.4, §19.4). */
export function cardPlayability(
  s: MatchState,
  side: Side,
  cardId: string,
  onlyPawnId?: string,
): Playability {
  const card = s.cards[cardId];
  if (!card || !s.players[side].hand.includes(cardId))
    return { playable: false, reason: 'NOT_IN_HAND' };
  if (card.iou) return { playable: false, reason: 'NO_MATCH' };
  let pawns = movablePawns(s, side);
  if (onlyPawnId !== undefined) pawns = pawns.filter((p) => p.id === onlyPawnId);
  if (card.trick) {
    if (!s.ledger || !cardMatches(s, side, card, card.face as Face))
      return { playable: false, reason: 'NO_MATCH' };
    return trickOptions(s, side, card, pawns).length > 0
      ? { playable: true }
      : { playable: false, reason: 'NO_LEGAL_MOVE' };
  }
  const ctx = boardCtx(s);
  if (card.kind === 'normal') {
    const face = card.face as Face;
    if (!cardMatches(s, side, card, face) || !firstLinkColorOk(s, side, face))
      return { playable: false, reason: 'NO_MATCH' };
    return pawns.some((p) => hasLitMove(s, ctx, p, face))
      ? { playable: true }
      : { playable: false, reason: 'NO_LEGAL_MOVE' };
  }
  const faces = ALL_FACES.filter((f) => firstLinkColorOk(s, side, f));
  let anyCollateral = false;
  for (const pawn of pawns) {
    const { onBoard, reserves, free } = collateralChoices(s, side, pawn.id);
    if (onBoard.length + reserves.length > 0 || free) anyCollateral = true;
    const ctxs: BoardCtx[] = onBoard.map((id) =>
      staysAsCollateral(s, s.pawns[id]) ? ctx : withoutPawn(ctx, id),
    );
    if (reserves.length > 0 || free) ctxs.push(ctx);
    for (const cctx of ctxs)
      for (const face of faces) if (hasLitMove(s, cctx, pawn, face)) return { playable: true };
  }
  return { playable: false, reason: anyCollateral ? 'NO_LEGAL_MOVE' : 'NO_COLLATERAL' };
}

/** True when some card in hand is playable as the next link (§9.1 step 3). */
export function hasPlayableLink(s: MatchState, side: Side): boolean {
  return s.players[side].hand.some((id) => cardPlayability(s, side, id).playable);
}

export const canRecover = (s: MatchState, side: Side): boolean =>
  s.players[side].hand.length > 0 && s.players[side].deck.length > 0;

export const epitaphPlaysLeft = (s: MatchState, side: Side): boolean =>
  s.players[side].epitaphPlays < epitaphPlays(s, side);

/** True when the side has any legal action in the ACTION phase (§6.1 step 3). */
export function hasAnyAction(s: MatchState, side: Side): boolean {
  if (canRecover(s, side)) return true;
  if (hasPlayableLink(s, side)) return true;
  if (epitaphPlaysLeft(s, side))
    for (const e of s.players[side].epitaphs)
      for (const pawn of onBoardPawns(s.pawns, side))
        if (checkEpitaphReplay(s, side, e, pawn.id).ok) return true;
  return false;
}

// ---------------------------------------------------------------------------------------------
// Epitaph replays (§10.4, §11.3)
// ---------------------------------------------------------------------------------------------

export interface ReplayStep {
  readonly link: RecordedLink;
  readonly from: Square;
  readonly option: MoveOption;
  readonly crowned: boolean;
  /** Where the pawn stepped out of a Looking Glass after this link (LEDGER.MD §6). */
  readonly glass?: Square;
}

export type ReplayCheck =
  | {
      readonly ok: true;
      readonly steps: readonly ReplayStep[];
      readonly start: Square;
      readonly end: Square;
      readonly captures: readonly string[];
      readonly crowned: boolean;
      readonly breakdown: ScoreBreakdown;
      readonly face: Face;
      /** Long Night: what each replayed link did, for the Tally. */
      readonly links?: readonly LedgerLink[];
    }
  | {
      readonly ok: false;
      /** Index of the first recorded link that cannot be replayed; -1 for an invalid pawn. */
      readonly failedAt: number;
      /** IN_THE_DARK: Blind Eye; the pawn stands, or a step would land, in the dark. */
      readonly reason: MoveFailure | 'INVALID_PAWN' | 'IN_THE_DARK';
    };

/** Omens a Tally reads, as they lie on a list of landing squares (previews of replays). */
function omensLandedOn(s: MatchState, landings: readonly Square[]): LedgerLink['omens'] {
  const out: { id: number; kind: LedgerLink['omens'][number]['kind'] }[] = [];
  for (const sq of landings)
    for (const o of omensAt(s, sq))
      if (!o.shrouded && o.kind !== 'pillar' && o.kind !== 'salt-circle' && o.kind !== 'thorns')
        out.push({ id: o.id, kind: o.kind });
  return out;
}

/** Simulates replaying an Epitaph with a pawn, without touching the state. */
export function checkEpitaphReplay(
  s: MatchState,
  side: Side,
  epitaph: Epitaph,
  pawnId: string,
): ReplayCheck {
  let pawn = s.pawns[pawnId];
  if (!pawn || pawn.side !== side || pawn.status !== 'onBoard' || !pawn.square)
    return { ok: false, failedAt: -1, reason: 'INVALID_PAWN' };
  if (s.ledger && hasStatus(s, pawnId, 'pinned'))
    return { ok: false, failedAt: -1, reason: 'INVALID_PAWN' };
  if (s.ledger && inTheDark(s, pawn.square))
    return { ok: false, failedAt: -1, reason: 'IN_THE_DARK' };
  // A route this side cannot read (Veiled) has no steps to replay.
  if (epitaph.inscription.links.length === 0)
    return { ok: false, failedAt: 0, reason: 'WRONG_SHAPE' };
  const start = pawn.square;
  const crownedAtStart = pawn.crowned;
  const pawns: Record<string, Pawn> = { ...s.pawns };
  const base = boardCtx(s);
  const occ = base.occ.slice();
  const visited = new Set<string>([squareKey(start)]);
  const landings: Square[] = [];
  const captures: string[] = [];
  const steps: ReplayStep[] = [];
  const ledgerLinks: LedgerLink[] = [];
  let newLandings = 0;
  let crowned = false;
  const links = epitaph.inscription.links;
  for (let i = 0; i < links.length; i++) {
    const link = links[i] as RecordedLink;
    const ctx: BoardCtx = {
      occ,
      pawns,
      immunity: s.config.homeRowImmunity,
      ...(base.mods ? { mods: base.mods } : {}),
    };
    const r = checkRecordedMove(ctx, pawn, link.face, link.move);
    if (!r.ok) return { ok: false, failedAt: i, reason: r.reason };
    if (s.ledger && r.option.landings.some((sq) => inTheDark(s, sq)))
      return { ok: false, failedAt: i, reason: 'IN_THE_DARK' };
    const from = pawn.square as Square;
    const caps = s.ledger
      ? r.option.captures.map((id) => {
          const p = pawns[id] as Pawn;
          return {
            id,
            inscribed: !!p.inscription && !hasStatus(s, id, 'hollow'),
            exposed: hasStatus(s, id, 'exposed'),
          };
        })
      : [];
    for (const capId of r.option.captures) {
      const cap = pawns[capId] as Pawn;
      occ[squareIndex(cap.square as Square)] = null;
      pawns[capId] = { ...cap, status: 'captured', square: null };
      captures.push(capId);
    }
    const to = r.option.landings[r.option.landings.length - 1] as Square;
    const crownedNow = crownsAt(pawn, to);
    occ[squareIndex(from)] = null;
    occ[squareIndex(to)] = pawn.id;
    pawn = { ...pawn, square: to, crowned: pawn.crowned || crownedNow };
    pawns[pawn.id] = pawn;
    let fresh = 0;
    for (const l of r.option.landings) {
      landings.push(l);
      const k = squareKey(l);
      if (!visited.has(k)) {
        visited.add(k);
        newLandings++;
        fresh++;
      }
    }
    if (crownedNow) crowned = true;
    // A link that ends on a Looking Glass steps out of the one opposite when that square is free,
    // as in a chain played with cards (stepThroughGlass): the next link starts from there.
    const twin = s.ledger ? glassTwin(s, to) : undefined;
    const glass =
      twin && !occ[squareIndex(twin.square)] && !omenAt(s, twin.square, 'pillar')
        ? twin.square
        : undefined;
    if (glass) {
      occ[squareIndex(to)] = null;
      occ[squareIndex(glass)] = pawn.id;
      pawn = { ...pawn, square: glass };
      pawns[pawn.id] = pawn;
    }
    steps.push({ link, from, option: r.option, crowned: crownedNow, ...(glass ? { glass } : {}) });
    if (s.ledger)
      ledgerLinks.push({
        cardId: null,
        face: link.face,
        wasWild: link.wasWild,
        landings: r.option.landings,
        newLandings: fresh,
        captures: caps,
        crowned: crownedNow,
        path: pathLength(r.option.move),
        omens: omensLandedOn(s, r.option.landings),
      });
  }
  const end = pawn.square as Square;
  const last = links[links.length - 1] as RecordedLink;
  const breakdown = s.ledger
    ? tally(
        { ...s, pawns },
        {
          side,
          source: 'epitaph',
          pawnId,
          links: ledgerLinks,
          start,
          end,
          crownedAtStart,
          crownedAtEnd: pawn.crowned,
          topBefore: topFace(s),
          epitaphSource: epitaph.sourcePawnId,
          pileSize: s.pile.length + 1,
        },
        'preview',
      ).breakdown
    : scoreChain(
        {
          faces: links.map((l) => l.face),
          newLandings,
          captures: captures.length,
          crowned,
          start,
          end,
          landings,
        },
        s.config.constants,
        s.config.crownMode,
      );
  return {
    ok: true,
    steps,
    start,
    end,
    captures,
    crowned,
    breakdown,
    face: last.face,
    ...(s.ledger ? { links: ledgerLinks } : {}),
  };
}

// ---------------------------------------------------------------------------------------------
// Resolution helpers
// ---------------------------------------------------------------------------------------------

export function makeRouteStep(
  face: Face,
  wasWild: boolean,
  from: Square,
  option: MoveOption,
  crowned: boolean,
): RouteStep {
  return {
    face,
    wasWild,
    from,
    landings: option.landings,
    captures: option.captures,
    captureAt: option.captureAt,
    crowned,
    notation: linkNotation(face, wasWild, from, option.landings, option.captureAt, crowned),
  };
}

function capturePawn(s: MatchState, capId: string, byPawnId: string, ev: Events): void {
  const cap = s.pawns[capId] as Pawn;
  const square = cap.square as Square;
  s.pawns[capId] = { ...cap, status: 'captured', square: null };
  let epitaphId: string | null = null;
  if (s.ledger) {
    // Long Night: a Hollow pawn leaves nothing; a Haunted one leaves its route to its captor.
    const to = epitaphRecipient(s, cap);
    if (to) {
      const epitaph: Epitaph = {
        id: `EP${s.nextEpitaphSeq}`,
        owner: to.owner,
        sourcePawnId: capId,
        inscription: to.inscription,
      };
      s.nextEpitaphSeq += 1;
      epitaphId = epitaph.id;
      queueArrival(s, epitaph);
    }
    ev.push({ type: 'PawnCaptured', side: cap.side, pawnId: capId, byPawnId, square, epitaphId });
    afterCapture(s, cap, byPawnId, ev);
    return;
  }
  if (cap.inscription) {
    // §10.2: the owner of an inscribed pawn receives its Epitaph.
    const epitaph: Epitaph = {
      id: `EP${s.nextEpitaphSeq}`,
      owner: cap.side,
      sourcePawnId: capId,
      inscription: cap.inscription,
    };
    s.nextEpitaphSeq += 1;
    epitaphId = epitaph.id;
    const prior = s.pendingArrivals;
    if (prior && prior.owner !== cap.side)
      throw new Error('Epitaph arrivals for both sides in one action');
    s.pendingArrivals = { owner: cap.side, epitaphs: [...(prior?.epitaphs ?? []), epitaph] };
  }
  ev.push({ type: 'PawnCaptured', side: cap.side, pawnId: capId, byPawnId, square, epitaphId });
}

/** Moves a pawn along a legal option: captures, then the move. Returns whether it crowned. */
function performMove(s: MatchState, pawnId: string, option: MoveOption, ev: Events): boolean {
  for (const capId of option.captures) capturePawn(s, capId, pawnId, ev);
  const pawn = s.pawns[pawnId] as Pawn;
  const to = option.landings[option.landings.length - 1] as Square;
  const crowned = crownsAt(pawn, to);
  s.pawns[pawnId] = { ...pawn, square: to, crowned: pawn.crowned || crowned };
  return crowned;
}

/** Crowning consequences (§8.6, §11.5). */
function handleCrowning(s: MatchState, side: Side, pawnId: string, ev: Events): void {
  const pawn = s.pawns[pawnId] as Pawn;
  ev.push({ type: 'PawnCrowned', side, pawnId, square: pawn.square as Square });
  if (s.ledger) afterCrowning(s, side, pawnId, ev, (st, sd) => drawIfRoom(st, sd, ev));
  if (s.config.crownMode !== 'delayed') return;
  const amount = s.config.constants.POINTS_CROWN;
  ev.push({ type: 'CrownBonusCreated', side, pawnId, amount });
  if (s.players[opponent(side)].passed) {
    s.players[side].roundScore += amount;
    ev.push({ type: 'CrownBonusResolved', side, pawnId, outcome: 'awarded', amount });
  } else {
    s.players[side].pendingCrownBonuses.push(pawnId);
  }
}

// ---------------------------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------------------------

/** Pays a Wild's collateral (§9.4); the Long Night's free and Many Lives payments too. */
function payCollateral(s: MatchState, side: Side, lo: LinkOption, ev: Events): void {
  const pl = s.players[side];
  const colId = lo.collateralPawnId as string;
  if (s.ledger && colId === FREE_COLLATERAL) {
    useFreeWild(s, side);
    return;
  }
  const col = s.pawns[colId];
  if (s.ledger && col && staysAsCollateral(s, col)) {
    markOnce(s, `many-lives:${colId}`);
  } else if (col && col.status === 'onBoard') {
    s.pawns[colId] = { ...col, status: 'pawned', square: null };
    ev.push({
      type: 'PawnPawned',
      side,
      pawnId: colId,
      square: col.square,
      reserve: false,
      cardId: lo.cardId,
    });
  } else {
    pl.reservesPawnedThisRound.push(colId);
    ev.push({
      type: 'PawnPawned',
      side,
      pawnId: colId,
      square: null,
      reserve: true,
      cardId: lo.cardId,
    });
  }
  if (!pl.collateralUsed.includes(colId)) pl.collateralUsed.push(colId);
  if (s.ledger) afterPawning(s, side, col, ev);
}

/** Plays one link (§9.1 step 2) without the chain-continuation check. */
export function resolveLink(s: MatchState, side: Side, lo: LinkOption, ev: Events): void {
  const pl = s.players[side];
  const pawn = s.pawns[lo.pawnId] as Pawn;
  const from = pawn.square as Square;
  // A link made from a lit Brazier burns its card: it leaves the match, the pile stays as it was.
  const burn = litBrazier(s, side);
  if (lo.wasWild) payCollateral(s, side, lo, ev);
  if (!s.chain) {
    s.chain = {
      side,
      pawnId: lo.pawnId,
      startSquare: from,
      links: [],
      cardIds: [],
      landings: [],
      visited: [squareKey(from)],
      newLandings: 0,
      captures: 0,
      crownedDuringChain: false,
      ...(s.ledger ? { ledgerLinks: [], crownedAtStart: pawn.crowned } : {}),
    };
  }
  const chain = s.chain;
  // A Trick's link lands nowhere.
  const to = lo.option.landings[lo.option.landings.length - 1];
  const willCrown = !!to && crownsAt(pawn, to);
  ev.push({
    type: 'LinkPlayed',
    side,
    pawnId: lo.pawnId,
    cardId: lo.cardId,
    linkIndex: chain.links.length,
    step: makeRouteStep(lo.face, lo.wasWild, from, lo.option, willCrown),
    collateralPawnId: lo.collateralPawnId,
  });
  const caps = s.ledger ? captureInfo(s, lo.option) : [];
  const crowned = lo.trick ? false : performMove(s, lo.pawnId, lo.option, ev);
  const handIndex = pl.hand.indexOf(lo.cardId);
  pl.hand.splice(handIndex, 1);
  if (burn) {
    pl.removed.push(lo.cardId);
    chain.burned = (chain.burned ?? 0) + 1;
    ev.push({
      type: 'CardBurned',
      side,
      cardId: lo.cardId,
      card: s.cards[lo.cardId] as Card,
      pawnId: lo.pawnId,
      square: from,
    });
  } else
    s.pile.push({
      source: 'card',
      cardId: lo.cardId,
      face: lo.face,
      wasWild: lo.wasWild,
      playedBy: side,
    });
  chain.links.push({ face: lo.face, wasWild: lo.wasWild, move: lo.option.move });
  chain.cardIds.push(lo.cardId);
  let fresh = 0;
  for (const l of lo.option.landings) {
    chain.landings.push(l);
    const k = squareKey(l);
    if (!chain.visited.includes(k)) {
      chain.visited.push(k);
      chain.newLandings += 1;
      fresh += 1;
    }
  }
  chain.captures += lo.option.captures.length;
  if (s.ledger) {
    const record = afterLinkMove(
      s,
      side,
      lo.pawnId,
      from,
      lo.face,
      lo.wasWild,
      lo.cardId,
      lo.option,
      caps,
      crowned,
      fresh,
      ev,
      (st, sd) => drawIfRoom(st, sd, ev),
    );
    // A drawn card or a returned pawn may have replaced the chain object's owner state.
    const ch = s.chain as typeof chain;
    ch.ledgerLinks = [...(ch.ledgerLinks ?? []), record];
    if (record.omens.some((o) => o.kind === 'ink-pool')) ch.forceInscribe = true;
    if (lo.trick)
      applyTrick(s, side, lo.cardId, lo.pawnId, lo.trick.id, lo.trick.target, ev, (st, sd) =>
        drawIfRoom(st, sd, ev),
      );
    else stepThroughGlass(s, side, lo.pawnId, ev);
  }
  if (crowned) {
    chain.crownedDuringChain = true;
    handleCrowning(s, side, lo.pawnId, ev);
  }
}

/** The pile's top before a chain's first entry (Snap). */
function topBeforeChain(s: MatchState): Face | null {
  const chain = s.chain;
  if (!chain) return topFace(s);
  const k = chain.cardIds.length - (chain.burned ?? 0) + (chain.epitaphId ? 1 : 0);
  return s.pile[s.pile.length - k - 1]?.face ?? null;
}

/** The Tally input of the chain in progress (Long Night). */
function chainTallyInput(s: MatchState): TallyInput {
  const chain = s.chain as NonNullable<MatchState['chain']>;
  const pawn = s.pawns[chain.pawnId] as Pawn;
  return {
    side: chain.side,
    source: 'chain',
    pawnId: chain.pawnId,
    links: chain.ledgerLinks ?? [],
    start: chain.startSquare,
    end: pawn.square,
    crownedAtStart: chain.crownedAtStart ?? false,
    crownedAtEnd: pawn.crowned,
    topBefore: topBeforeChain(s),
    pileSize: s.pile.length,
  };
}

/** The chain's score if it ended now (§11.1); chances are not rolled. */
export function currentChainScore(s: MatchState): ScoreBreakdown | null {
  const chain = s.chain;
  if (!chain) return null;
  const pawn = s.pawns[chain.pawnId] as Pawn;
  if (s.ledger) return tally(s, chainTallyInput(s), 'preview').breakdown;
  return scoreChain(
    {
      faces: chain.links.map((l) => l.face),
      newLandings: chain.newLandings,
      captures: chain.captures,
      crowned: chain.crownedDuringChain,
      start: chain.startSquare,
      end: pawn.square,
      landings: chain.landings,
    },
    s.config.constants,
    s.config.crownMode,
  );
}

/** After a link: continue the chain or end it automatically (§9.1 step 3). */
export function afterLink(s: MatchState, side: Side, ev: Events): void {
  const chain = s.chain;
  if (!chain) throw new Error('afterLink without a chain');
  if (chain.links.length >= maxLinks(s, side) || !hasPlayableLink(s, side)) {
    endChain(s, ev);
    return;
  }
  s.phase = 'CHAIN';
  s.decider = side;
}

/** Scores the chain in progress with the Tally and applies what it changes (Long Night). */
function scoreLedgerChain(s: MatchState, ev: Events): ScoreBreakdown {
  const chain = s.chain as NonNullable<MatchState['chain']>;
  const side = chain.side;
  const pl = s.players[side];
  const res = tally(s, chainTallyInput(s), 'final');
  if (res.luck) s.ledger = { ...L(s), luck: res.luck };
  pl.roundScore = Math.min(1e100, pl.roundScore + res.breakdown.score);
  ev.push({
    type: 'ChainScored',
    side,
    pawnId: chain.pawnId,
    source: 'chain',
    breakdown: res.breakdown,
    roundScore: pl.roundScore,
  });
  applyTallyEffects(s, side, res.effects, ev);
  const links = chain.ledgerLinks ?? [];
  afterChainScored(
    s,
    side,
    links,
    res.breakdown.multiplier.pattern,
    res.breakdown.multiplier.secondPattern,
  );
  endOfMove(s, side, chain.pawnId, links, ev);
  return res.breakdown;
}

/** Chain end (§9.2): score, inscription, Epitaph arrivals. */
export function endChain(s: MatchState, ev: Events): void {
  const chain = s.chain;
  if (!chain) throw new Error('endChain without a chain');
  const side = chain.side;
  const pl = s.players[side];
  let breakdown: ScoreBreakdown;
  if (s.ledger) breakdown = scoreLedgerChain(s, ev);
  else {
    breakdown = currentChainScore(s) as ScoreBreakdown;
    pl.roundScore += breakdown.score;
    ev.push({
      type: 'ChainScored',
      side,
      pawnId: chain.pawnId,
      source: 'chain',
      breakdown,
      roundScore: pl.roundScore,
    });
  }
  // A Trick's link moved nothing, so it is not part of the route (LEDGER.MD §12.2).
  const inscription: Inscription = {
    links: chain.links.filter((l) => l.move.type !== 'still'),
    recordedScore: breakdown.score,
    round: s.round,
  };
  s.chain = null;
  const pawn = s.pawns[chain.pawnId] as Pawn;
  if (s.ledger && (hasStatus(s, pawn.id, 'hollow') || inscription.links.length === 0)) {
    resolveArrivals(s, ev);
    return;
  }
  if (!pawn.inscription || (s.ledger && chain.forceInscribe)) {
    s.pawns[pawn.id] = { ...pawn, inscription };
    ev.push({
      type: 'InscriptionSet',
      side,
      pawnId: pawn.id,
      how: pawn.inscription ? 'replaced' : 'automatic',
      inscription,
    });
    if (s.ledger) twinSoul(s, pawn.id, inscription, ev);
    resolveArrivals(s, ev);
    return;
  }
  s.pendingInscription = { pawnId: pawn.id, inscription };
  s.phase = 'CHOOSE_INSCRIPTION';
  s.decider = side;
}

export function chooseInscription(s: MatchState, replace: boolean, ev: Events): void {
  const pending = s.pendingInscription;
  if (!pending) throw new Error('no pending inscription');
  const pawn = s.pawns[pending.pawnId] as Pawn;
  if (replace) {
    s.pawns[pawn.id] = { ...pawn, inscription: pending.inscription };
    ev.push({
      type: 'InscriptionSet',
      side: pawn.side,
      pawnId: pawn.id,
      how: 'replaced',
      inscription: pending.inscription,
    });
  } else {
    ev.push({
      type: 'InscriptionSet',
      side: pawn.side,
      pawnId: pawn.id,
      how: 'kept',
      inscription: pawn.inscription as Inscription,
    });
  }
  s.pendingInscription = null;
  resolveArrivals(s, ev);
}

/** After one owner's arrivals: the next owner's (Long Night), or the end of the turn. */
function afterArrivals(s: MatchState, ev: Events): void {
  if (s.ledger && nextArrivals(s)) {
    resolveArrivals(s, ev);
    return;
  }
  endTurn(s, ev);
}

/** Epitaph arrivals (§10.3), resolved once the acting side's action has fully resolved. */
export function resolveArrivals(s: MatchState, ev: Events): void {
  const arrivals = s.pendingArrivals;
  s.pendingArrivals = null;
  if (!arrivals || arrivals.epitaphs.length === 0) {
    afterArrivals(s, ev);
    return;
  }
  const owner = arrivals.owner;
  const pl = s.players[owner];
  if (pl.epitaphs.length + arrivals.epitaphs.length <= epitaphSlots(s, owner)) {
    for (const epitaph of arrivals.epitaphs) {
      pl.epitaphs.push(epitaph);
      ev.push({ type: 'EpitaphReceived', side: owner, epitaph, how: 'automatic' });
      if (s.ledger) onEpitaphReceived(s, owner, ev);
    }
    afterArrivals(s, ev);
    return;
  }
  s.pendingEpitaphChoice = { owner, candidates: [...pl.epitaphs, ...arrivals.epitaphs], kept: [] };
  s.phase = 'CHOOSE_EPITAPH';
  s.decider = owner;
}

export function chooseEpitaph(s: MatchState, keepId: string, ev: Events): void {
  const choice = s.pendingEpitaphChoice;
  if (!choice) throw new Error('no pending Epitaph choice');
  const kept = [...choice.kept, keepId];
  const needed = Math.min(epitaphSlots(s, choice.owner), choice.candidates.length);
  if (kept.length < needed) {
    s.pendingEpitaphChoice = { ...choice, kept };
    return;
  }
  const pl = s.players[choice.owner];
  const held = new Set(pl.epitaphs.map((e) => e.id));
  const keep = new Set(kept);
  pl.epitaphs = choice.candidates.filter((e) => keep.has(e.id));
  for (const epitaph of choice.candidates) {
    if (!keep.has(epitaph.id)) ev.push({ type: 'EpitaphDiscarded', side: choice.owner, epitaph });
    else if (!held.has(epitaph.id)) {
      ev.push({ type: 'EpitaphReceived', side: choice.owner, epitaph, how: 'chosen' });
      if (s.ledger) onEpitaphReceived(s, choice.owner, ev);
    }
  }
  s.pendingEpitaphChoice = null;
  afterArrivals(s, ev);
}

/**
 * Plays a validated Epitaph replay (§10.4). In the Long Night it may open a chain (Séance Table),
 * and a Planchette replays one from the pile without using the side's slot or play.
 */
export function playEpitaph(
  s: MatchState,
  side: Side,
  epitaph: Epitaph,
  pawnId: string,
  ev: Events,
  opts: { readonly chain?: boolean; readonly fromSlot?: boolean } = {},
): void {
  const check = checkEpitaphReplay(s, side, epitaph, pawnId);
  if (!check.ok) throw new Error('playEpitaph called with an illegal replay');
  const pl = s.players[side];
  const fromSlot = opts.fromSlot ?? true;
  ev.push({
    type: 'EpitaphPlayed',
    side,
    pawnId,
    epitaph,
    route: check.steps.map((st) =>
      makeRouteStep(st.link.face, st.link.wasWild, st.from, st.option, st.crowned),
    ),
    score: check.breakdown.score,
  });
  if (!s.ledger) {
    for (const step of check.steps) {
      const crowned = performMove(s, pawnId, step.option, ev);
      if (crowned) handleCrowning(s, side, pawnId, ev);
    }
    pl.roundScore += check.breakdown.score;
    ev.push({
      type: 'ChainScored',
      side,
      pawnId,
      source: 'epitaph',
      breakdown: check.breakdown,
      roundScore: pl.roundScore,
    });
    s.pile.push({ source: 'epitaph', epitaphId: epitaph.id, face: check.face, playedBy: side });
    pl.epitaphs = pl.epitaphs.filter((e) => e.id !== epitaph.id);
    pl.epitaphPlays += 1;
    resolveArrivals(s, ev);
    return;
  }
  // Long Night: the replay is made link by link, Omens and all, then counted by the Tally.
  const start = check.start;
  const crownedAtStart = (s.pawns[pawnId] as Pawn).crowned;
  const visited = new Set<string>([squareKey(start)]);
  const links: LedgerLink[] = [];
  const allLandings: Square[] = [];
  let captures = 0;
  let crownedAny = false;
  const draw = (st: MatchState, sd: Side) => drawIfRoom(st, sd, ev);
  for (const step of check.steps) {
    const caps = captureInfo(s, step.option);
    const crowned = performMove(s, pawnId, step.option, ev);
    let fresh = 0;
    for (const l of step.option.landings) {
      allLandings.push(l);
      const k = squareKey(l);
      if (!visited.has(k)) {
        visited.add(k);
        fresh++;
      }
    }
    captures += step.option.captures.length;
    links.push(
      afterLinkMove(
        s,
        side,
        pawnId,
        step.from,
        step.link.face,
        step.link.wasWild,
        null,
        step.option,
        caps,
        crowned,
        fresh,
        ev,
        draw,
      ),
    );
    if (step.glass) stepThroughGlass(s, side, pawnId, ev);
    if (crowned) {
      crownedAny = true;
      handleCrowning(s, side, pawnId, ev);
    }
  }
  s.pile.push({ source: 'epitaph', epitaphId: epitaph.id, face: check.face, playedBy: side });
  const l = L(s);
  s.ledger = { ...l, pileEpitaphs: { ...l.pileEpitaphs, [epitaph.id]: epitaph } };
  if (fromSlot) {
    pl.epitaphs = pl.epitaphs.filter((e) => e.id !== epitaph.id);
    pl.epitaphPlays += 1;
  }
  bumpCounter(s, side, 'epitaphsThisTable');
  if (opts.chain) {
    // Séance Table: the replay is the start of a chain that cards may continue.
    s.chain = {
      side,
      pawnId,
      startSquare: start,
      links: epitaph.inscription.links.slice(),
      cardIds: [],
      landings: allLandings,
      visited: [...visited],
      newLandings: visited.size - 1,
      captures,
      crownedDuringChain: crownedAny,
      ledgerLinks: links,
      crownedAtStart,
      epitaphId: epitaph.id,
    };
    const pawn = s.pawns[pawnId];
    if (pawn?.status === 'onBoard') {
      afterLink(s, side, ev);
      return;
    }
    endChain(s, ev);
    return;
  }
  const pawn = s.pawns[pawnId] as Pawn;
  const res = tally(
    s,
    {
      side,
      source: 'epitaph',
      pawnId,
      links,
      start,
      end: pawn.square,
      crownedAtStart,
      crownedAtEnd: pawn.crowned,
      topBefore: s.pile[s.pile.length - 2]?.face ?? null,
      epitaphSource: epitaph.sourcePawnId,
      pileSize: s.pile.length,
    },
    'final',
  );
  if (res.luck) s.ledger = { ...L(s), luck: res.luck };
  pl.roundScore = Math.min(1e100, pl.roundScore + res.breakdown.score);
  ev.push({
    type: 'ChainScored',
    side,
    pawnId,
    source: 'epitaph',
    breakdown: res.breakdown,
    roundScore: pl.roundScore,
  });
  applyTallyEffects(s, side, res.effects, ev);
  endOfMove(s, side, pawnId, links, ev);
  resolveArrivals(s, ev);
}

export function recover(s: MatchState, side: Side, cardId: string, ev: Events): void {
  const pl = s.players[side];
  pl.hand.splice(pl.hand.indexOf(cardId), 1);
  pl.removed.push(cardId);
  ev.push({
    type: 'Recovered',
    side,
    cardId,
    card: s.cards[cardId] as MatchState['cards'][string],
  });
  const drawn = drawCard(s, side, 'recover', ev);
  if (s.ledger) onRecoverDraw(s, side, drawn, ev);
  endTurn(s, ev);
}

/** Draws a card if the hand has room (Wells, Pockets, Bell Ringer's kind of draws). */
const drawIfRoom = (s: MatchState, side: Side, ev: Events): void => {
  if (s.players[side].hand.length < handLimit(s, side)) drawCard(s, side, 'draw', ev);
};

/** Draws the top card of a side's deck into its hand; returns its id. */
export function drawCard(
  s: MatchState,
  side: Side,
  reason: 'setup' | 'draw' | 'recover',
  ev: Events,
): string | undefined {
  const pl = s.players[side];
  const cardId = pl.deck.shift();
  if (cardId === undefined) return undefined;
  pl.hand.push(cardId);
  ev.push({ type: 'CardDrawn', side, reason, cardId, card: s.cards[cardId] ?? null });
  return cardId;
}

export function dealStartingHands(s: MatchState, ev: Events): void {
  for (const side of ['S', 'N'] as const) {
    // Long Night: Short Hand keeps the starting hand within the lowered limit.
    const n = Math.min(s.config.constants.HAND_START, handLimit(s, side));
    for (let i = 0; i < n; i++) drawCard(s, side, 'setup', ev);
  }
}

/** CONTINUE: draw step, then the ACTION phase or a forced pass (§6.1). */
export function continueTurn(s: MatchState, side: Side, ev: Events): void {
  const pl = s.players[side];
  if (pl.hand.length < handLimit(s, side) && pl.deck.length > 0) drawCard(s, side, 'draw', ev);
  if (!hasAnyAction(s, side)) {
    passSide(s, side, 'forced', ev);
    endTurn(s, ev);
    return;
  }
  s.phase = 'ACTION';
  s.decider = side;
}

export function passSide(s: MatchState, side: Side, kind: PassKind, ev: Events): void {
  const pl = s.players[side];
  pl.passed = true;
  pl.passKind = kind;
  if (s.ledger) notePass(s, side);
  // Out of turns: which rule, and how many turns it allows, so the reason can be told.
  const limit = kind === 'outOfTurns' ? turnLimitRule(s, side) : null;
  ev.push({ type: 'Passed', side, kind, round: s.round, ...(limit ? { limit } : {}) });
}

// ---------------------------------------------------------------------------------------------
// Turn, round and match flow (§4–§6)
// ---------------------------------------------------------------------------------------------

export function beginTurn(s: MatchState, side: Side, ev: Events): void {
  s.turn += 1;
  s.active = side;
  s.decider = side;
  s.phase = 'TURN_START';
  ev.push({ type: 'TurnStarted', side, round: s.round, turn: s.turn });
  resolveCrownBonuses(s, side, ev);
  // Long Night: Homing pawns come back, and Short Wick or Clock may end the side's round.
  const outOfTurns = s.ledger ? turnStart(s, side, ev) : false;
  // After Night III: the boss's power, at the start of its turns (LEDGER.MD §13.1).
  if (s.ledger) horrorsAtTurnStart(s, side, ev);
  const pawnsLeft = onBoardPawns(s.pawns, side).length > 0;
  if (!pawnsLeft || outOfTurns) {
    passSide(s, side, pawnsLeft ? 'outOfTurns' : 'automatic', ev);
    endTurn(s, ev);
  }
}

/** Variant B: resolve the side's pending crown bonuses at its turn start (§11.5). */
function resolveCrownBonuses(s: MatchState, side: Side, ev: Events): void {
  const pl = s.players[side];
  if (pl.pendingCrownBonuses.length === 0) return;
  const amount = s.config.constants.POINTS_CROWN;
  for (const pawnId of pl.pendingCrownBonuses) {
    const pawn = s.pawns[pawnId];
    if (pawn && pawn.status === 'onBoard') {
      pl.roundScore += amount;
      ev.push({ type: 'CrownBonusResolved', side, pawnId, outcome: 'awarded', amount });
    } else {
      ev.push({ type: 'CrownBonusResolved', side, pawnId, outcome: 'discarded', amount });
    }
  }
  pl.pendingCrownBonuses = [];
}

export function endTurn(s: MatchState, ev: Events): void {
  s.chain = null;
  s.pendingInscription = null;
  s.pendingArrivals = null;
  s.pendingEpitaphChoice = null;
  if (s.ledger) {
    turnEnd(s, s.active, ev);
    endTrickStatuses(s, s.active);
  }
  if (s.players.S.passed && s.players.N.passed) {
    endRound(s, ev);
    return;
  }
  const other = opponent(s.active);
  // Long Night: an Hourglass skips the other side's next turn.
  if (s.ledger && !s.players[other].passed && !s.players[s.active].passed && skipsTurn(s, other)) {
    beginTurn(s, s.active, ev);
    return;
  }
  beginTurn(s, s.players[other].passed ? s.active : other, ev);
}

function endRound(s: MatchState, ev: Events): void {
  const scores = { S: s.players.S.roundScore, N: s.players.N.roundScore };
  const winner: Side | null = scores.S > scores.N ? 'S' : scores.N > scores.S ? 'N' : null;
  if (winner) s.players[winner].roundWins += 1;
  s.players.S.totalScore = Math.min(1e100, s.players.S.totalScore + scores.S);
  s.players.N.totalScore = Math.min(1e100, s.players.N.totalScore + scores.N);
  s.rounds.push({ round: s.round, starter: s.roundStarter, scores, winner });
  ev.push({
    type: 'RoundEnded',
    round: s.round,
    scores,
    winner,
    totals: { S: s.players.S.totalScore, N: s.players.N.totalScore },
    roundWins: { S: s.players.S.roundWins, N: s.players.N.roundWins },
  });
  if (s.ledger) {
    noteFallen(s);
    roundEnd(s, winner, ev);
    endTrickStatuses(s, 'S');
    endTrickStatuses(s, 'N');
  }
  const result = resultAfterRound(s);
  if (result) {
    finishMatch(s, result, ev);
    return;
  }
  setupRound(s, s.round + 1, ev);
}

/** Match end after a round (§4.3). */
function resultAfterRound(s: MatchState): MatchResult | null {
  const c = s.config.constants;
  const wS = s.players.S.roundWins;
  const wN = s.players.N.roundWins;
  if (wS >= c.ROUND_WINS_NEEDED) return { winner: 'S', reason: 'roundWins' };
  if (wN >= c.ROUND_WINS_NEEDED) return { winner: 'N', reason: 'roundWins' };
  if (s.round < c.MAX_ROUNDS) return null;
  if (wS !== wN) return { winner: wS > wN ? 'S' : 'N', reason: 'roundWins' };
  const tS = s.players.S.totalScore;
  const tN = s.players.N.totalScore;
  if (tS !== tN) return { winner: tS > tN ? 'S' : 'N', reason: 'totalScore' };
  return s.config.mode === 'campaign'
    ? { winner: 'N', reason: 'brokerTie' }
    : { winner: null, reason: 'draw' };
}

export function finishMatch(s: MatchState, result: MatchResult, ev: Events): void {
  s.result = result;
  s.phase = 'MATCH_OVER';
  s.chain = null;
  s.pendingInscription = null;
  s.pendingArrivals = null;
  s.pendingEpitaphChoice = null;
  // Long Night: the Keepsake Locket remembers what was held.
  if (s.ledger)
    s.ledger = {
      ...s.ledger,
      finalEpitaphs: { S: s.players.S.epitaphs.slice(), N: s.players.N.epitaphs.slice() },
    };
  // §10.3: Epitaph slots are cleared at match end.
  s.players.S.epitaphs = [];
  s.players.N.epitaphs = [];
  // Long Night: borrowed Superstitions go home.
  if (s.ledger) returnStolen(s, ev, () => true);
  ev.push({ type: 'MatchEnded', result });
}

/** Round setup (§5.1). */
export function setupRound(s: MatchState, round: number, ev: Events): void {
  s.round = round;
  s.turn = 0;
  for (const id of Object.keys(s.pawns)) {
    const p = s.pawns[id] as Pawn;
    s.pawns[id] = {
      ...p,
      square: p.startSquare,
      status: 'onBoard',
      crowned: false,
      inscription: null,
    };
  }
  // Long Night: cards with a Return Stamp go back to their owner's deck instead of Spent.
  const returned = s.ledger ? returningCards(s) : [];
  for (const entry of s.pile) {
    if (entry.source !== 'card') continue; // Epitaphs on the pile are discarded.
    if (returned.includes(entry.cardId)) continue;
    const owner = s.cards[entry.cardId]?.owner;
    if (owner) s.players[owner].spent.push(entry.cardId);
  }
  s.pile = [];
  for (const side of ['S', 'N'] as const) {
    const pl = s.players[side];
    pl.roundScore = side === 'N' ? s.config.northHeadStart : 0;
    pl.passed = false;
    pl.passKind = null;
    pl.epitaphPlays = 0;
    pl.pendingCrownBonuses = [];
    pl.reservesPawnedThisRound = [];
  }
  s.chain = null;
  s.pendingInscription = null;
  s.pendingArrivals = null;
  s.pendingEpitaphChoice = null;
  ev.push({
    type: 'RoundStarted',
    round,
    scores: { S: s.players.S.roundScore, N: s.players.N.roundScore },
  });
  if (s.ledger) roundSetup(s, round, returned, ev);
  if (round === 1) {
    const rng = Pcg32.fromState(s.rng.match);
    const starter: Side = rng.nextBelow(2) === 0 ? 'S' : 'N';
    (s as { rng: MatchState['rng'] }).rng = { ...s.rng, match: rng.toState() };
    startRound(s, starter, 'random', null, ev);
    return;
  }
  const prev = s.rounds[s.rounds.length - 1];
  if (prev?.winner) {
    const loser = opponent(prev.winner);
    s.phase = 'CHOOSE_STARTER';
    s.active = loser;
    s.decider = loser;
    return;
  }
  startRound(s, opponent(prev?.starter ?? s.roundStarter), 'tieRule', null, ev);
}

export function startRound(
  s: MatchState,
  starter: Side,
  how: 'random' | 'loserChoice' | 'tieRule',
  chooser: Side | null,
  ev: Events,
): void {
  s.roundStarter = starter;
  ev.push({ type: 'StarterChosen', round: s.round, starter, how, chooser });
  beginTurn(s, starter, ev);
}

export function concede(s: MatchState, side: Side, ev: Events): void {
  ev.push({ type: 'Conceded', side });
  finishMatch(s, { winner: opponent(side), reason: 'concede' }, ev);
}

/** True when `a` is a square in `list`. */
export const squareIn = (a: Square, list: readonly Square[]): boolean =>
  list.some((b) => sameSquare(a, b));
